import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import PolicyTable from '../components/policies/PolicyTable';
import AddPolicyModal from '../components/policies/AddPolicyModal';
import PolicyModal from '../components/policies/PolicyModal';
import OpportunityTable from '../components/opportunities/OpportunityTable';
import OpportunityModal from '../components/opportunities/OpportunityModal';
import AddOpportunityModal from '../components/opportunities/AddOpportunityModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import TaskList from '../components/Tasks/TaskList';
import CreateTaskModal from '../components/Tasks/CreateTaskModal';
import TaskDetailModal from '../components/Tasks/TaskDetailModal';
import Infobar from '../components/profile-components/Infobar';
import EmailComposer from '../components/profile-components/EmailComposer';
import ProfileMergeModal from '../components/profile-components/ProfileMergeModal';
import DocumentManager from '../components/documents/DocumentManager';
import SendSOAModal from '../components/SOA/SendSOAModal';
import SendACAConsentModal from '../components/ACAConsent/SendACAConsentModal';
import AddressVerificationFields from '../components/common/AddressVerificationFields';
import SpouseSelector from '../components/profile-components/SpouseSelector';
import './LeadProfile.css';
import './SalesPages.css';
import '../styles/Tabs.css';
import { authFetch } from '../utils/authFetch';
import usePageTitle from '../hooks/usePageTitle';
import CustomFieldsSection from '../components/custom-fields/CustomFieldsSection';

function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [client, setClient] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [recordHistory, setRecordHistory] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [activeTab, setActiveTab] = useState('Information');
  const [mobileTabsOpen, setMobileTabsOpen] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSOAModal, setShowSOAModal] = useState(false);
  const [showACAModal, setShowACAModal] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const [confirmText, setConfirmText] = useState('');
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [saving, setSaving] = useState(false);

  const [healthSherpaSettings, setHealthSherpaSettings] = useState({
    enabled: false,
    brokerUrl: '',
  });

  const mergeData = {
    first_name: client.first_name,
    last_name: client.last_name,
    full_name: `${client.first_name || ''} ${client.last_name || ''}`.trim(),
  };

  const tabs = ['Information', 'Notes', 'Policies', 'Opportunities', 'Tasks', 'Call Logs', 'Record History'];

  const handleTabChange = (tab) => {
    setShowEmailComposer(false);
    setActiveTab(tab);
    setMobileTabsOpen(false);
  };

  const formatDate = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val.split('T')[0];
    if (typeof val === 'object' && val.toISOString) return val.toISOString().split('T')[0];
    return '';
  };

  const normalizeDate = (dateStr) => {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  };

  const safeFetch = async (path, options = {}, label = 'Request') => {
    try {
      const res = await authFetch(path, options);
      const type = res.headers.get('content-type') || '';

      if (!res.ok) {
        const text = await res.text();
        console.error(`❌ ${label} failed (${res.status}):`, text);
        return null;
      }

      if (type.includes('application/json')) {
        return await res.json();
      }

      return null;
    } catch (err) {
      console.error(`💥 ${label} error:`, err);
      return null;
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setShowEmailComposer(false);
      setActiveTab(tab);
      setMobileTabsOpen(false);
    }
  }, [searchParams]);

  useEffect(() => {
    const oppId = searchParams.get('opportunityId');

    if (oppId && opportunities.length > 0) {
      const foundOpp = opportunities.find((o) => String(o.id) === String(oppId));

      if (foundOpp) {
        setSelectedOpportunity(foundOpp);
        setShowOpportunityModal(true);
        setActiveTab('Opportunities');
      }
    }
  }, [opportunities, searchParams]);

  useEffect(() => {
    const taskId = searchParams.get('taskId');

    if (!taskId || tasks.length === 0) {
      return;
    }

    const foundTask = tasks.find((task) => String(task.id) === String(taskId));

    if (!foundTask) {
      return;
    }

    setShowEmailComposer(false);
    setActiveTab('Tasks');
    setMobileTabsOpen(false);
    setSelectedTask(foundTask);
    setShowTaskModal(true);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('taskId');
    setSearchParams(newParams, { replace: true });
  }, [tasks, searchParams, setSearchParams]);

  useEffect(() => {
    const loadAll = async () => {
      const [
        clientData,
        notesData,
        historyData,
        policyData,
        oppData,
        taskData
      ] = await Promise.all([
        safeFetch(`/api/clients/${id}`, {}, 'Client'),
        safeFetch(`/api/notes/client/${id}`, {}, 'Client Notes'),
        safeFetch(`/api/record-history/client/${id}`, {}, "History"),
        safeFetch(`/api/policies/${id}`, {}, 'Policies'),
        safeFetch(`/api/opportunities/client/${id}`, {}, 'Opportunities'),
        safeFetch(`/api/tasks?relatedType=client&relatedId=${id}`, {}, 'Tasks'),
      ]);

      if (clientData) setClient(clientData);
      if (Array.isArray(notesData)) setNotes(notesData);
      if (Array.isArray(historyData)) setRecordHistory(historyData);
      if (Array.isArray(policyData)) setPolicies(policyData);
      if (Array.isArray(oppData)) setOpportunities(oppData);
      if (Array.isArray(taskData)) setTasks(taskData);
    };

    loadAll();
  }, [id]);

  useEffect(() => {
    const loadDropdowns = async () => {
      const [s, src, c, u] = await Promise.all([
        safeFetch('/api/lead-statuses', {}, 'Statuses'),
        safeFetch('/api/lead-sources', {}, 'Sources'),
        safeFetch('/api/carriers', {}, 'Carriers'),
        safeFetch('/api/users', {}, 'Users'),
      ]);

      if (Array.isArray(s)) setStatuses(s);
      if (Array.isArray(src)) setSources(src);
      if (Array.isArray(c)) setCarriers(c);
      if (Array.isArray(u)) setUsers(u);
    };

    loadDropdowns();
  }, []);

  useEffect(() => {
    const loadHealthSherpaSettings = async () => {
      const data = await safeFetch('/api/settings/healthsherpa', {}, 'HealthSherpa settings fetch');

      if (!data) return;

      setHealthSherpaSettings({
        enabled:
          data.healthsherpa_enabled === true ||
          data.healthsherpa_enabled === 1 ||
          data.healthsherpa_enabled === '1',
        brokerUrl: data.healthsherpa_broker_url || '',
      });
    };

    loadHealthSherpaSettings();
  }, []);

  useEffect(() => {
    const shouldCreateOpp = searchParams.get('createOpportunity');

    if (shouldCreateOpp === 'true') {
      setSelectedOpportunity(null);
      setShowOpportunityModal(true);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('createOpportunity');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleChange = (e) => {
    setClient({
      ...client,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddressChange = (fieldName, value) => {
    setClient((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const clientToSave = {
      ...client,
      dob: normalizeDate(client.dob),
      part_a_date: normalizeDate(client.part_a_date),
      part_b_date: normalizeDate(client.part_b_date),
      address_verified: Number(client.address_verified) ? 1 : 0,
    };

    const res = await safeFetch(
      `/api/clients/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientToSave),
      },
      'Update Client'
    );

    if (res) alert('✅ Client updated');
    setSaving(false);
  };

  const handleDeleteClient = async () => {
    const res = await safeFetch(
      `/api/clients/${id}`,
      { method: 'DELETE' },
      'Delete Client'
    );
    if (res) navigate('/clients');
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    const res = await safeFetch(
      `/api/notes/client/${id}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: newNote }),
      },
      'Add Note'
    );

    if (res) {
      setNotes([{ id: Date.now(), note: newNote, created_at: new Date().toISOString() }, ...notes]);
      setNewNote('');
    }
  };

  const handleOpenEmailComposer = (email) => {
    setEmailRecipient(email || client.email || '');
    setShowEmailComposer(true);
    setMobileTabsOpen(false);
  };

  const handleCloseEmailComposer = () => {
    setShowEmailComposer(false);
    setEmailRecipient(null);
  };

  const handleQuoteACA = async () => {
    if (!healthSherpaSettings.enabled || !healthSherpaSettings.brokerUrl) {
      alert('HealthSherpa quoting is not enabled for your user account.');
      return;
    }

    const data = await safeFetch(
      `/api/healthsherpa/launch-link?recordType=client&recordId=${id}`,
      {},
      'HealthSherpa launch link'
    );

    if (!data?.launch_url) {
      alert('Unable to open HealthSherpa quoting link.');
      return;
    }

    window.open(data.launch_url, '_blank', 'noopener,noreferrer');
  };

  const handlePolicySubmit = async (form) => {
  const payload = {
    status: form.status || null,

    carrierId: form.carrier_id || form.carrierId || null,
    carrier_id: form.carrier_id || form.carrierId || null,

    productId: form.product_id || form.productId || null,
    product_id: form.product_id || form.productId || null,

    planName: form.plan_name || form.planName || null,
    plan_name: form.plan_name || form.planName || null,

    planId: form.plan_id || form.planId || null,
    plan_id: form.plan_id || form.planId || null,

    coverageTypeId: form.coverage_type_id || form.coverageTypeId || null,
    coverage_type_id: form.coverage_type_id || form.coverageTypeId || null,

    coverageTypePlanTypeId:
      form.coverage_type_plan_type_id || form.coverageTypePlanTypeId || null,
    coverage_type_plan_type_id:
      form.coverage_type_plan_type_id || form.coverageTypePlanTypeId || null,

    applicationDate: form.application_date || form.applicationDate || null,
    application_date: form.application_date || form.applicationDate || null,

    effectiveDate: form.effective_date || form.effectiveDate || null,
    effective_date: form.effective_date || form.effectiveDate || null,

    renewalDate: form.renewal_date || form.renewalDate || null,
    renewal_date: form.renewal_date || form.renewalDate || null,

    terminationDate: form.termination_date || form.terminationDate || null,
    termination_date: form.termination_date || form.terminationDate || null,

    premium: form.premium || null,

    policyNumber: form.policy_number || form.policyNumber || null,
    policy_number: form.policy_number || form.policyNumber || null,

    enrollmentCode: form.enrollment_code || form.enrollmentCode || null,
    enrollment_code: form.enrollment_code || form.enrollmentCode || null,

    electionPeriod: form.election_period || form.electionPeriod || null,
    election_period: form.election_period || form.electionPeriod || null,

    hraCompleted: form.hra_completed || form.hraCompleted || null,
    hra_completed: form.hra_completed || form.hraCompleted || null,

    benefitOption: form.benefit_option || form.benefitOption || null,
    benefit_option: form.benefit_option || form.benefitOption || null,

    faceValue: form.face_value || form.faceValue || null,
    face_value: form.face_value || form.faceValue || null,

    benefitAmount: form.benefit_amount || form.benefitAmount || null,
    benefit_amount: form.benefit_amount || form.benefitAmount || null,

    termLength: form.term_length || form.termLength || null,
    term_length: form.term_length || form.termLength || null,

    planYear: form.plan_year || form.planYear || null,
    plan_year: form.plan_year || form.planYear || null,

    recordType: 'policy',
    record_type: 'policy',

    leadId: form.lead_id || form.leadId || null,
    lead_id: form.lead_id || form.leadId || null,

    clientId: id,
    client_id: id,
  };

  const res = await safeFetch(
    `/api/policies/${id}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    'Add Policy'
  );

  if (res) {
    setPolicies((prev) => [...prev, res]);
    setSelectedPolicy(null);
    setShowPolicyModal(false);
    return res;
  }

  return null;
};

  const handleUpdatePolicy = async (obj) => {
    const res = await safeFetch(
      `/api/policies/${obj.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(obj),
      },
      'Update Policy'
    );

    if (res) {
      setPolicies((prev) => prev.map((p) => (p.id === res.id ? res : p)));
      setSelectedPolicy(null);
      setShowPolicyModal(false);
    }
  };

  const handleDeletePolicy = async (pid, coverageTypeId) => {
    const res = await safeFetch(
      `/api/policies/${pid}/${coverageTypeId}`,
      {
        method: 'DELETE',
      },
      'Delete Policy'
    );

    if (res !== null) {
      setPolicies((prev) => prev.filter((p) => p.id !== pid));
      setSelectedPolicy(null);
      setShowPolicyModal(false);
    }
  };

  const handleOpportunitySubmit = async (form) => {
    const res = await safeFetch(
      '/api/opportunities',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, client_id: id }),
      },
      'Add Opportunity'
    );

    if (res) {
      setOpportunities((prev) => [...prev, res]);
      setSelectedOpportunity(null);
      setShowOpportunityModal(false);
      return res;
    }

    return null;
  };

  const handleUpdateOpportunity = async (opp) => {
    const res = await safeFetch(
      `/api/opportunities/${opp.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opp),
      },
      'Update Opportunity'
    );

    if (res) {
      setOpportunities((prev) => prev.map((o) => (o.id === res.id ? res : o)));
      setSelectedOpportunity(res);

      if (!res.shouldPromptConversion) {
        setSelectedOpportunity(null);
        setShowOpportunityModal(false);
      }

      return res;
    }

    return null;
  };

  const handleDeleteOpportunity = async (oid) => {
    const res = await safeFetch(
      `/api/opportunities/${oid}`,
      {
        method: 'DELETE',
      },
      'Delete Opportunity'
    );

    if (res !== null) {
      setOpportunities((prev) => prev.filter((x) => x.id !== oid));
      setSelectedOpportunity(null);
      setShowOpportunityModal(false);
    }
  };

  const handleAddTask = () => setShowCreateTaskModal(true);

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const fullName =
    `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Client Profile';

  usePageTitle(fullName);

  const statusName = useMemo(() => {
    const match = statuses.find((s) => String(s.id) === String(client.lead_status_id));
    return match?.name || '';
  }, [statuses, client.lead_status_id]);

  const sourceName = useMemo(() => {
    const match = sources.find((s) => String(s.id) === String(client.lead_source_id));
    return match?.name || '';
  }, [sources, client.lead_source_id]);

  const aorName = useMemo(() => {
    const match = users.find((u) => String(u.id) === String(client.aor_user_id));
    return match?.name || '';
  }, [users, client.aor_user_id]);

  const activeCoverageTypes = useMemo(() => {
    return [...new Set(
      policies
        .filter((policy) => {
          const status = String(policy.status || '').toLowerCase().trim();
          return status === 'active';
        })
        .map((policy) => policy.coverageTypeName)
        .filter(Boolean)
    )];
  }, [policies]);

  const canQuoteACA = Boolean(
    healthSherpaSettings.enabled && healthSherpaSettings.brokerUrl
  );

  const statusChipClass = (value) => {
    const normalized = String(value || 'unknown').toLowerCase().replace(/\s+/g, '_');
    return `sales-chip ${normalized}`;
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sales-hero sales-profile-hero">
              <div>
                <p className="sales-eyebrow">Agent Book CRM</p>
                <h1 className="sales-title">{fullName}</h1>
                <p className="sales-subtitle">
                  Manage client details, policies, opportunities, tasks, notes, and record activity.
                </p>

                <div className="sales-hero-meta">
                  <span className={statusChipClass(statusName || 'active')}>
                    {statusName || 'No Status'}
                  </span>

                  {sourceName && (
                    <span className="sales-chip sales-chip-neutral">{sourceName}</span>
                  )}

                  {aorName && (
                    <span className="sales-chip sales-chip-outline">AOR: {aorName}</span>
                  )}

                  {activeCoverageTypes.map((coverageType) => (
                    <span key={coverageType} className="sales-chip sales-chip-outline">
                      {coverageType}
                    </span>
                  ))}
                </div>
              </div>

              <div className="sales-header-actions">
                <button
                  type="button"
                  className="sales-primary-btn"
                  onClick={() => { }}
                >
                  Convert to Winback
                </button>

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={handleAddTask}
                >
                  Add Task
                </button>

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => setShowSOAModal(true)}
                >
                  Send SOA
                </button>

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => setShowACAModal(true)}
                >
                  Send ACA
                </button>

                {canQuoteACA && (
                  <button
                    type="button"
                    className="sales-secondary-btn"
                    onClick={handleQuoteACA}
                  >
                    Quote ACA
                  </button>
                )}

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => handleOpenEmailComposer(client.email)}
                >
                  Send Email
                </button>

                <button
                  type="submit"
                  form="client-profile-form"
                  className="sales-secondary-btn"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            <div className="profile-mobile-tab-menu">
              <button
                type="button"
                className="profile-mobile-tab-button"
                onClick={() => setMobileTabsOpen((prev) => !prev)}
              >
                <span>{activeTab}</span>
                <span className="profile-mobile-tab-icon">
                  {mobileTabsOpen ? '▲' : '▼'}
                </span>
              </button>

              {mobileTabsOpen && (
                <div className="profile-mobile-tab-options">
                  {tabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      className={activeTab === tab ? 'active' : ''}
                      onClick={() => handleTabChange(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="tabs profile-desktop-tabs">
              {tabs.map((tab) => (
                <div
                  key={tab}
                  className={`tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </div>
              ))}
            </div>

            <div className="tab-content">
              {showEmailComposer ? (
                <EmailComposer
                  recipientEmail={emailRecipient || client.email || ''}
                  recipientName={fullName}
                  clientId={client.id}
                  mergeData={mergeData}
                  onClose={handleCloseEmailComposer}
                />
              ) : (
                <>
                  {activeTab === 'Information' && (
                    <form id="client-profile-form" onSubmit={handleSubmit}>
                      <div className="sales-profile-grid">
                        <div className="sales-section-card sales-section-lead">
                          <div className="sales-section-head">
                            <h2>Client Information</h2>
                            <p>Status, source, ownership, personal details, and Medicare fields.</p>
                          </div>

                          <div className="sales-form-fields">
                            <div className="sales-field">
                              <label>Status</label>
                              <select name="lead_status_id" value={client.lead_status_id || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Source</label>
                              <select name="lead_source_id" value={client.lead_source_id || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Referred By</label>
                              <input name="referred_by" value={client.referred_by || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>AOR</label>
                              <select name="aor_user_id" value={client.aor_user_id || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>First Name</label>
                              <input name="first_name" value={client.first_name || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Last Name</label>
                              <input name="last_name" value={client.last_name || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Marital Status</label>
                              <select name="marital_status" value={client.marital_status || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                <option value="Single">Single</option>
                                <option value="Married">Married</option>
                                <option value="Separated">Separated</option>
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Spouse</label>
                              <SpouseSelector
                                recordType="client"
                                record={client}
                                onLinked={(spouseData) => {
                                  setClient((prev) => ({
                                    ...prev,
                                    ...spouseData
                                  }));
                                }}
                                onUnlinked={() => {
                                  setClient((prev) => ({
                                    ...prev,
                                    spouse_record_type: null,
                                    spouse_record_id: null,
                                    spouse_name: ''
                                  }));
                                }}
                              />
                            </div>

                            <div className="sales-field">
                              <label>DOB</label>
                              <input type="date" name="dob" value={formatDate(client.dob)} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Gender</label>
                              <select name="gender" value={client.gender || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                <option>Male</option>
                                <option>Female</option>
                                <option>Other</option>
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Medicaid Number</label>
                              <input name="medicaid_number" value={client.medicaid_number || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Medicare Number</label>
                              <input name="medicare_number" value={client.medicare_number || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Part A Date</label>
                              <input type="date" name="part_a_date" value={formatDate(client.part_a_date)} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Part B Date</label>
                              <input type="date" name="part_b_date" value={formatDate(client.part_b_date)} onChange={handleChange} />
                            </div>

                            <CustomFieldsSection
                              recordType="client"
                              recordId={client.id}
                            />
                          </div>
                        </div>

                        <div className="sales-section-card sales-section-subscription">
                          <div className="sales-section-head">
                            <h2>Contact Details</h2>
                            <p>Address and contact information for this client.</p>
                          </div>

                          <div className="sales-form-fields">
                            <div className="sales-field">
                              <label>Phone</label>
                              <input name="phone" value={client.phone || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Email</label>
                              <input name="email" value={client.email || ''} onChange={handleChange} />
                            </div>

                            <AddressVerificationFields
                              values={client}
                              onChange={handleAddressChange}
                              fieldMap={{
                                street: 'street_address',
                                unit: 'unit',
                                city: 'city',
                                state: 'state',
                                zip: 'zip_code',
                                county: 'county',
                                addressVerified: 'address_verified',
                              }}
                            />

                            <input
                              type="hidden"
                              name="address_verified"
                              value={client.address_verified || 0}
                            />
                          </div>
                        </div>
                      </div>
                    </form>
                  )}

                  {activeTab === 'Notes' && (
                    <div className="sales-section-card sales-section-lead">
                      <div className="sales-section-head" style={{ marginBottom: '14px' }}>
                        <h2>Notes</h2>
                        <p>Capture context, follow-ups, and communication history for this client.</p>
                      </div>

                      <form onSubmit={handleNoteSubmit} style={{ marginBottom: '18px' }}>
                        <div className="sales-field">
                          <label>Add Note</label>
                          <textarea
                            className="sales-note-textarea"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add a concise note, follow-up detail, or internal comment..."
                          />
                        </div>

                        <div style={{ marginTop: '12px' }}>
                          <button type="submit" className="sales-primary-btn">
                            Add Note
                          </button>
                        </div>
                      </form>

                      {notes.length > 0 ? (
                        <div className="profile-note-list">
                          {notes.map((n) => {
                            const isEmail = n.content?.startsWith('📧 Email Sent');

                            return (
                              <div key={n.id} className="profile-note-card">
                                <div className="profile-note-card-header">
                                  <div className="profile-note-type">
                                    {isEmail ? 'Email Activity' : 'Note'}
                                  </div>

                                  <div className="profile-note-date">
                                    {new Date(n.created_at).toLocaleString()}
                                  </div>
                                </div>

                                {isEmail ? (
                                  <div
                                    className="profile-note-email-content"
                                    dangerouslySetInnerHTML={{ __html: n.content }}
                                  />
                                ) : (
                                  <div className="profile-note-body">
                                    {n.note}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="profile-empty-card">
                          No notes available for this client.
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'Policies' && (
                    <div className="sales-table-card">
                      <div className="sales-table-header">
                        <div>
                          <h2>Policies</h2>
                          <p>{policies.length} polic{policies.length === 1 ? 'y' : 'ies'}</p>
                        </div>

                        <button
                          type="button"
                          className="sales-primary-btn"
                          onClick={() => {
                            setSelectedPolicy(null);
                            setShowPolicyModal(true);
                          }}
                        >
                          + Add Policy
                        </button>
                      </div>

                      <div className="sales-table-wrap">
                        <PolicyTable
                          policies={policies}
                          onEdit={(p) => {
                            setSelectedPolicy(p);
                            setShowPolicyModal(true);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'Opportunities' && (
                    <div className="sales-table-card">
                      <div className="sales-table-header">
                        <div>
                          <h2>Opportunities</h2>
                          <p>{opportunities.length} opportunit{opportunities.length === 1 ? 'y' : 'ies'}</p>
                        </div>

                        <button
                          type="button"
                          className="sales-primary-btn"
                          onClick={() => {
                            setSelectedOpportunity(null);
                            setShowOpportunityModal(true);
                          }}
                        >
                          + Add Opportunity
                        </button>
                      </div>

                      <div className="sales-table-wrap">
                        <OpportunityTable
                          opportunities={opportunities}
                          onEdit={(opp) => {
                            setSelectedOpportunity(opp);
                            setShowOpportunityModal(true);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'Tasks' && (
                    <div className="sales-table-card">
                      <div className="sales-table-header">
                        <div>
                          <h2>Tasks</h2>
                          <p>{tasks.length} task{tasks.length === 1 ? '' : 's'}</p>
                        </div>

                        <button
                          type="button"
                          className="sales-primary-btn"
                          onClick={handleAddTask}
                        >
                          + Add Task
                        </button>
                      </div>

                      <div className="sales-table-wrap">
                        <TaskList
                          relatedId={client.id}
                          relatedType="client"
                          onTaskClick={handleTaskClick}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'Call Logs' && (
                    <div className="sales-section-card">
                      <div className="sales-section-head">
                        <h2>Call Logs</h2>
                        <p>Call logging UI can be added here without changing the tab structure.</p>
                      </div>

                      <p style={{ margin: 0, color: '#64748b' }}>
                        No call logs available yet.
                      </p>
                    </div>
                  )}

                  {activeTab === 'Record History' && (
                    <div className="sales-profile-grid">
                      <div className="sales-section-card sales-section-lead">
                        <div className="sales-section-head" style={{ marginBottom: '14px' }}>
                          <h2>Record History</h2>
                          <p>A chronological timeline of important actions and activity on this record.</p>
                        </div>

                        {recordHistory.length > 0 ? (
                          <div className="profile-activity-list">
                            {recordHistory.map((r) => (
                              <div key={r.id} className="profile-activity-card">
                                <div className="profile-activity-accent" />

                                <div className="profile-activity-type">
                                  Activity
                                </div>

                                <div className="profile-activity-body">
                                  {r.action}
                                </div>

                                <div className="profile-activity-date">
                                  {new Date(r.created_at).toLocaleString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="profile-empty-card">
                            No record history available.
                          </div>
                        )}
                      </div>

                      <div className="sales-section-card sales-section-subscription">
                        <div className="sales-section-head" style={{ marginBottom: '14px' }}>
                          <h2>Documents</h2>
                          <p>Stored files and generated documents associated with this client.</p>
                        </div>

                        <DocumentManager recordId={client.id} recordType="client" />

                          <div style={{ marginTop: '18px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="sales-secondary-btn"
                              onClick={() => setShowMergeModal(true)}
                              disabled={!client.id}
                            >
                              Merge Profile
                            </button>

                            <button
                              type="button"
                              onClick={() => setShowDeleteModal(true)}
                              className="sales-danger-btn"
                            >
                              Delete Profile
                            </button>
                          </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <Infobar
            record={client}
            relatedId={client.id}
            relatedType="client"
            onEmailClick={handleOpenEmailComposer}
            onOpenPolicyOrOppModal={(item) => {
              setShowEmailComposer(false);
              setMobileTabsOpen(false);

              if (item.type === 'policy') {
                setSelectedPolicy(item);
                setShowPolicyModal(true);
                setActiveTab('Policies');
              } else {
                setSelectedOpportunity(item);
                setShowOpportunityModal(true);
                setActiveTab('Opportunities');
              }
            }}
          />
        </div>
      </div>

      {showPolicyModal && (
        selectedPolicy ? (
          <PolicyModal
            policy={selectedPolicy}
            products={products}
            carriers={carriers}
            onSave={handleUpdatePolicy}
            onDelete={handleDeletePolicy}
            onClose={() => {
              setSelectedPolicy(null);
              setShowPolicyModal(false);
            }}
          />
        ) : (
          <AddPolicyModal
            clientId={id}
            products={products}
            onSubmit={handlePolicySubmit}
            onClose={() => {
              setSelectedPolicy(null);
              setShowPolicyModal(false);
            }}
          />
        )
      )}

      {showOpportunityModal && (
        selectedOpportunity ? (
          <OpportunityModal
            opportunity={selectedOpportunity}
            products={products}
            onSave={handleUpdateOpportunity}
            onDelete={handleDeleteOpportunity}
            onClose={() => {
              setSelectedOpportunity(null);
              setShowOpportunityModal(false);
            }}
          />
        ) : (
          <AddOpportunityModal
            clientId={id}
            products={products}
            onSubmit={handleOpportunitySubmit}
            onClose={() => {
              setSelectedOpportunity(null);
              setShowOpportunityModal(false);
            }}
          />
        )
      )}

      {showDeleteModal && (
        <DeleteConfirmationModal
          onConfirm={handleDeleteClient}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {showSOAModal && (
        <SendSOAModal
          client={client}
          onClose={() => setShowSOAModal(false)}
        />
      )}

      {showACAModal && (
        <SendACAConsentModal
          record={client}
          recordType="client"
          onClose={() => setShowACAModal(false)}
        />
      )}

      {showTaskModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          source="profile"
          onUpdate={() => setShowTaskModal(false)}
          onClose={() => setShowTaskModal(false)}
        />
      )}

      {client.id && showCreateTaskModal && (
        <CreateTaskModal
          relatedId={client.id}
          relatedType="client"
          onCreated={() => setShowCreateTaskModal(false)}
          onClose={() => setShowCreateTaskModal(false)}
        />
      )}

      {client.id && showMergeModal && (
        <ProfileMergeModal
          recordType="client"
          primaryId={client.id}
          primaryName={fullName}
          onClose={() => setShowMergeModal(false)}
          onMerged={() => window.location.reload()}
        />
      )}
    </>
  );
}

export default ClientProfile;